import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, Upload, FileText, Download, Trash2, Search, Tag, FlaskConical, AlertTriangle } from 'lucide-react';
import { axiosInstance } from '@/App';
import { toast } from 'sonner';

const AdminPolicies = ({ user }) => {
  const { t } = useTranslation();
  const [policies, setPolicies] = useState([]);
  const [filteredPolicies, setFilteredPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('policies');
  
  // SDS State
  const [sdsDocuments, setSdsDocuments] = useState([]);
  const [filteredSds, setFilteredSds] = useState([]);
  const [loadingSds, setLoadingSds] = useState(false);
  const [sdsSearchTerm, setSdsSearchTerm] = useState('');
  const [showSdsUploadDialog, setShowSdsUploadDialog] = useState(false);
  const [uploadingSds, setUploadingSds] = useState(false);
  const [sdsUploadForm, setSdsUploadForm] = useState({
    file: null,
    files: [],  // For bulk upload
    chemicalName: '',
    manufacturer: ''
  });
  
  const [uploadForm, setUploadForm] = useState({
    title: '',
    category: 'Safety Policies',
    tags: '',
    description: '',
    file: null
  });

  const categories = [
    'All',
    'Safety Policies',
    'Standard Operating Procedures (SOPs)',
    'Emergency Response Manuals',
    'Training Materials',
    'Regulatory Compliance',
    'Other'
  ];

  useEffect(() => {
    loadPolicies();
    loadSdsDocuments();
  }, []);

  useEffect(() => {
    filterPolicies();
  }, [policies, selectedCategory, searchTerm]);

  useEffect(() => {
    filterSds();
  }, [sdsDocuments, sdsSearchTerm]);

  const loadPolicies = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/company-policies');
      setPolicies(response.data);
    } catch (error) {
      console.error('Failed to load policies:', error);
      toast.error(t('adminToasts.FailedToLoadPolicies'));
    } finally {
      setLoading(false);
    }
  };

  const filterPolicies = () => {
    let filtered = policies;
    
    if (selectedCategory !== 'All') {
      filtered = filtered.filter(p => p.category === selectedCategory);
    }
    
    if (searchTerm) {
      filtered = filtered.filter(p => 
        p.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase())) ||
        p.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    setFilteredPolicies(filtered);
  };

  // SDS Functions
  const loadSdsDocuments = async () => {
    try {
      setLoadingSds(true);
      const response = await axiosInstance.get('/sds/library');
      // Handle the response format from backend
      const docs = response.data.sds_documents || response.data || [];
      setSdsDocuments(docs);
    } catch (error) {
      console.error('Failed to load SDS documents:', error);
    } finally {
      setLoadingSds(false);
    }
  };

  const filterSds = () => {
    // Don't show any results until user searches
    if (!sdsSearchTerm || sdsSearchTerm.trim().length < 2) {
      setFilteredSds([]);
      return;
    }
    const searchLower = sdsSearchTerm.toLowerCase();
    const filtered = sdsDocuments.filter(doc =>
      doc.chemical_name?.toLowerCase().includes(searchLower) ||
      doc.manufacturer?.toLowerCase().includes(searchLower) ||
      doc.product_name?.toLowerCase().includes(searchLower) ||
      doc.cas_number?.toLowerCase().includes(searchLower)
    );
    setFilteredSds(filtered);
  };

  const handleSdsFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const pdfFiles = files.filter(f => f.name.toLowerCase().endsWith('.pdf'));
    
    if (pdfFiles.length === 0) {
      toast.error(t('adminToasts.OnlyPdfFilesAreSupportedForSds'));
      return;
    }
    
    if (pdfFiles.length === 1) {
      // Single file
      setSdsUploadForm({ ...sdsUploadForm, file: pdfFiles[0], files: [] });
    } else {
      // Multiple files (bulk)
      setSdsUploadForm({ ...sdsUploadForm, file: null, files: pdfFiles });
      toast.success(`${pdfFiles.length} PDF files selected for bulk upload`);
    }
  };

  const handleFolderSelect = (e) => {
    const files = Array.from(e.target.files);
    const pdfFiles = files.filter(f => f.name.toLowerCase().endsWith('.pdf'));
    
    if (pdfFiles.length === 0) {
      toast.error(t('adminToasts.NoPdfFilesFoundInSelectedFolder'));
      return;
    }
    
    setSdsUploadForm({ ...sdsUploadForm, file: null, files: pdfFiles });
    toast.success(`${pdfFiles.length} PDF files found for bulk upload`);
  };

  const cleanFileName = (filename) => {
    return filename
      .replace(/\.pdf$/i, '')
      .replace(/[-_]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  };

  const handleSdsUpload = async () => {
    const filesToUpload = sdsUploadForm.files.length > 0 ? sdsUploadForm.files : (sdsUploadForm.file ? [sdsUploadForm.file] : []);
    
    if (filesToUpload.length === 0) {
      toast.error(t('adminToasts.PleaseSelectAtLeastOnePdfFile'));
      return;
    }

    // For single file, require chemical name
    if (filesToUpload.length === 1 && !sdsUploadForm.chemicalName) {
      toast.error(t('adminToasts.PleaseProvideAChemicalProductName'));
      return;
    }

    setUploadingSds(true);
    let successCount = 0;
    let failCount = 0;

    try {
      for (const file of filesToUpload) {
        const formData = new FormData();
        formData.append('file', file);
        
        // For bulk upload, use filename as chemical name
        const chemicalName = filesToUpload.length === 1 
          ? sdsUploadForm.chemicalName 
          : cleanFileName(file.name);
        
        formData.append('chemical_name', chemicalName);
        // Only send manufacturer if it has a value, otherwise let backend handle defaults
        if (sdsUploadForm.manufacturer && sdsUploadForm.manufacturer.trim()) {
          formData.append('manufacturer', sdsUploadForm.manufacturer.trim());
        }

        try {
          const uploadResp = await axiosInstance.post('/sds/upload', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          if (uploadResp.data?.extraction_warning) {
            toast.warning(`${file.name}: ${uploadResp.data.extraction_warning}`, { duration: 9000 });
          }
          successCount++;
        } catch (err) {
          console.error(`Failed to upload ${file.name}:`, err);
          failCount++;
        }
      }

      if (successCount > 0) {
        toast.success(`Successfully uploaded ${successCount} SDS file(s)${failCount > 0 ? `, ${failCount} failed` : ''}`);
      } else {
        toast.error(t('adminToasts.AllUploadsFailed'));
      }

      setShowSdsUploadDialog(false);
      setSdsUploadForm({ file: null, files: [], chemicalName: '', manufacturer: '' });
      loadSdsDocuments();
    } catch (error) {
      console.error('SDS upload failed:', error);
      toast.error('Failed to upload SDS: ' + (error.response?.data?.detail || error.message));
    } finally {
      setUploadingSds(false);
    }
  };

  const handleSdsDelete = async (sdsId) => {
    if (!window.confirm('Are you sure you want to delete this SDS?')) return;

    try {
      await axiosInstance.delete(`/sds/${sdsId}`);
      toast.success(t('adminToasts.SdsDeletedSuccessfully'));
      loadSdsDocuments();
    } catch (error) {
      console.error('SDS delete failed:', error);
      toast.error(t('adminToasts.FailedToDeleteSds'));
    }
  };

  const handleSdsDownload = async (sdsId, productName) => {
    try {
      const response = await axiosInstance.get(`/sds/download/${sdsId}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${productName}_SDS.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(t('adminToasts.SdsDownloadedSuccessfully'));
    } catch (error) {
      console.error('SDS download failed:', error);
      toast.error(t('adminToasts.FailedToDownloadSds'));
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      const ext = file.name.split('.').pop().toLowerCase();
      if (!['pdf', 'docx', 'txt'].includes(ext)) {
        toast.error(t('adminToasts.OnlyPdfDocxAndTxtFilesAreSupported'));
        return;
      }
      setUploadForm({ ...uploadForm, file });
    }
  };

  const handleUpload = async () => {
    if (!uploadForm.title || !uploadForm.file) {
      toast.error(t('adminToasts.PleaseProvideATitleAndSelectAFile'));
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadForm.file);
      formData.append('title', uploadForm.title);
      formData.append('category', uploadForm.category);
      formData.append('tags', uploadForm.tags);
      formData.append('description', uploadForm.description);

      await axiosInstance.post('/company-policies/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success(t('adminToasts.PolicyUploadedSuccessfully'));
      setShowUploadDialog(false);
      setUploadForm({ title: '', category: 'Safety Policies', tags: '', description: '', file: null });
      loadPolicies();
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error('Failed to upload policy: ' + (error.response?.data?.detail || error.message));
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (policyId) => {
    if (!window.confirm('Are you sure you want to delete this policy?')) return;

    try {
      await axiosInstance.delete(`/company-policies/${policyId}`);
      toast.success(t('adminToasts.PolicyDeletedSuccessfully'));
      loadPolicies();
    } catch (error) {
      console.error('Delete failed:', error);
      toast.error(t('adminToasts.FailedToDeletePolicy'));
    }
  };

  const handleDownload = async (policyId, fileName) => {
    try {
      const response = await axiosInstance.get(`/company-policies/${policyId}/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(t('adminToasts.PolicyDownloadedSuccessfully'));
    } catch (error) {
      console.error('Download failed:', error);
      toast.error(t('adminToasts.FailedToDownloadPolicy'));
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      'Safety Policies': 'bg-red-100 text-red-800',
      'Standard Operating Procedures (SOPs)': 'bg-blue-100 text-blue-800',
      'Emergency Response Manuals': 'bg-orange-100 text-orange-800',
      'Training Materials': 'bg-green-100 text-green-800',
      'Regulatory Compliance': 'bg-purple-100 text-purple-800',
      'Other': 'bg-gray-100 text-gray-800'
    };
    return colors[category] || colors['Other'];
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6 flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => window.history.back()}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">{t('adminUI.PoliciesSdsLibrary')}</h1>
            <p className="text-gray-600 mt-1">{t('adminUI.ManageCompanyDocumentsSafetyDataSheetsAndProcedures')}</p>
          </div>
        </div>

        {/* Tabs for Policies and SDS */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full max-w-md grid-cols-2 mb-6">
            <TabsTrigger value="policies" className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Policies & Procedures
            </TabsTrigger>
            <TabsTrigger value="sds" className="flex items-center gap-2">
              <FlaskConical className="w-4 h-4" />
              SDS Library
            </TabsTrigger>
          </TabsList>

          {/* Policies Tab */}
          <TabsContent value="policies">

        {/* Stats and Upload */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">{t('adminUI.TotalPolicies')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{policies.length}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">{t('adminUI.Categories')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{new Set(policies.map(p => p.category)).size}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">{t('adminUI.TotalSize')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {formatFileSize(policies.reduce((sum, p) => sum + (p.fileSize || 0), 0))}
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-blue-500 to-purple-600 text-white">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-white">{t('adminUI.QuickAction')}</CardTitle>
            </CardHeader>
            <CardContent>
              <Button
                onClick={() => setShowUploadDialog(true)}
                className="w-full bg-white text-blue-600 hover:bg-gray-100"
              >
                <Upload className="w-4 h-4 mr-2" />
                Upload Policy
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search policies by title, tags, or description..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border rounded-lg"
                  />
                </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                {categories.map(cat => (
                  <Button
                    key={cat}
                    variant={selectedCategory === cat ? "default" : "outline"}
                    size="sm"
                    onClick={() => setSelectedCategory(cat)}
                  >
                    {cat}
                  </Button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Policies List */}
        {loading ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-gray-500">{t('adminUI.LoadingPolicies')}</p>
            </CardContent>
          </Card>
        ) : filteredPolicies.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <FileText className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500 mb-4">{t('adminUI.NoPoliciesFound')}</p>
              <Button onClick={() => setShowUploadDialog(true)}>
                <Upload className="w-4 h-4 mr-2" />
                Upload Your First Policy
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredPolicies.map(policy => (
              <Card key={policy.id} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-lg">{policy.title}</CardTitle>
                      <CardDescription className="mt-1">
                        {policy.fileName} • {formatFileSize(policy.fileSize)}
                      </CardDescription>
                    </div>
                    <FileText className="w-8 h-8 text-gray-400" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <Badge className={getCategoryColor(policy.category)}>
                      {policy.category}
                    </Badge>
                    
                    {policy.tags && policy.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {policy.tags.map((tag, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs">
                            <Tag className="w-3 h-3 mr-1" />
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    )}
                    
                    {policy.description && (
                      <p className="text-sm text-gray-600 line-clamp-2">{policy.description}</p>
                    )}
                    
                    <div className="text-xs text-gray-500">
                      Uploaded by {policy.uploadedByName} on {new Date(policy.uploadedAt).toLocaleDateString()}
                    </div>
                    
                    <div className="flex gap-2 pt-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDownload(policy.id, policy.fileName)}
                        className="flex-1"
                      >
                        <Download className="w-4 h-4 mr-1" />
                        Download
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDelete(policy.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Upload Dialog */}
        <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{t('adminUI.UploadCompanyPolicy')}</DialogTitle>
              <DialogDescription>{t('adminUI.UploadAPdfDocxOrTxtFileContainingYourCompanyPolicyOrProcedur')}</DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm font-medium">Title *</label>
                <input
                  type="text"
                  value={uploadForm.title}
                  onChange={(e) => setUploadForm({...uploadForm, title: e.target.value})}
                  placeholder="e.g., Lockout/Tagout Procedure"
                  className="w-full border rounded px-3 py-2 mt-1"
                />
              </div>

              <div>
                <label className="text-sm font-medium">Category *</label>
                <select
                  value={uploadForm.category}
                  onChange={(e) => setUploadForm({...uploadForm, category: e.target.value})}
                  className="w-full border rounded px-3 py-2 mt-1"
                >
                  {categories.filter(c => c !== 'All').map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-sm font-medium">Tags (comma-separated)</label>
                <input
                  type="text"
                  value={uploadForm.tags}
                  onChange={(e) => setUploadForm({...uploadForm, tags: e.target.value})}
                  placeholder="e.g., LOTO, electrical, machinery"
                  className="w-full border rounded px-3 py-2 mt-1"
                />
              </div>

              <div>
                <label className="text-sm font-medium">Description</label>
                <textarea
                  value={uploadForm.description}
                  onChange={(e) => setUploadForm({...uploadForm, description: e.target.value})}
                  placeholder="Brief description of the policy..."
                  rows={3}
                  className="w-full border rounded px-3 py-2 mt-1"
                />
              </div>

              <div>
                <label className="text-sm font-medium">File * (PDF, DOCX, TXT)</label>
                <input
                  type="file"
                  accept=".pdf,.docx,.txt"
                  onChange={handleFileSelect}
                  className="w-full border rounded px-3 py-2 mt-1"
                />
                {uploadForm.file && (
                  <p className="text-sm text-green-600 mt-1">
                    Selected: {uploadForm.file.name}
                  </p>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowUploadDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleUpload} disabled={uploading}>
                {uploading ? 'Uploading...' : 'Upload Policy'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
          </TabsContent>

          {/* SDS Library Tab */}
          <TabsContent value="sds">
            {/* SDS Stats and Upload */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">{t('adminUI.TotalSdsDocuments')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{sdsDocuments.length}</div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">{t('adminUI.Manufacturers')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {new Set(sdsDocuments.map(d => d.manufacturer).filter(Boolean)).size}
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-orange-500 to-red-600 text-white">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-white">{t('adminUI.QuickAction')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <Button
                    onClick={() => setShowSdsUploadDialog(true)}
                    className="w-full bg-white text-orange-600 hover:bg-gray-100"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Upload SDS
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* SDS Search */}
            <Card className="mb-6">
              <CardContent className="pt-6">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Type to search... (e.g., 'bleach', 'Oatey', 'acetone')"
                    value={sdsSearchTerm}
                    onChange={(e) => setSdsSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  />
                </div>
                {sdsSearchTerm && sdsSearchTerm.trim().length < 2 && (
                  <p className="text-xs text-gray-500 mt-2">{t('adminUI.TypeAtLeastCharactersToSearch')}</p>
                )}
              </CardContent>
            </Card>

            {/* SDS List */}
            {loadingSds ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <p className="text-gray-500">{t('adminUI.LoadingSdsDocuments')}</p>
                </CardContent>
              </Card>
            ) : filteredSds.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <FlaskConical className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                  {sdsSearchTerm && sdsSearchTerm.trim().length >= 2 ? (
                    <>
                      <p className="text-gray-500 mb-2">No SDS documents found for "{sdsSearchTerm}"</p>
                      <p className="text-sm text-gray-400">{t('adminUI.TryADifferentSearchTerm')}</p>
                    </>
                  ) : (
                    <>
                      <p className="text-gray-700 font-medium mb-2">{t('adminUI.SearchForSdsDocuments')}</p>
                      <p className="text-gray-500 mb-4">{t('adminUI.EnterAProductNameManufacturerOrChemicalToFindSafetyDataSheet')}</p>
                      <p className="text-sm text-gray-400">Total documents available: {sdsDocuments.length}</p>
                    </>
                  )}
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredSds.map(sds => (
                  <Card key={sds.id} className="hover:shadow-lg transition-shadow border-l-4 border-l-orange-500">
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <CardTitle className="text-lg">{sds.chemical_name}</CardTitle>
                          <CardDescription className="mt-1">
                            {sds.manufacturer || 'Unknown Manufacturer'}
                          </CardDescription>
                        </div>
                        <FlaskConical className="w-8 h-8 text-orange-500" />
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <Badge className="bg-orange-100 text-orange-800">
                          <AlertTriangle className="w-3 h-3 mr-1" />
                          Safety Data Sheet
                        </Badge>
                        
                        {sds.hazards && (sds.hazards.physical_hazards?.length > 0 || sds.hazards.health_hazards?.length > 0) && (
                          <div className="text-sm text-gray-600">
                            <strong>Hazards:</strong> {[
                              ...(sds.hazards.physical_hazards || []),
                              ...(sds.hazards.health_hazards || [])
                            ].slice(0, 3).join(', ')}
                            {[...(sds.hazards.physical_hazards || []), ...(sds.hazards.health_hazards || [])].length > 3 && '...'}
                          </div>
                        )}
                        
                        <div className="text-xs text-gray-500">
                          Uploaded {new Date(sds.uploaded_at).toLocaleDateString()}
                        </div>
                        
                        <div className="flex gap-2 pt-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleSdsDownload(sds.id, sds.chemical_name)}
                            className="flex-1"
                          >
                            <Download className="w-4 h-4 mr-1" />
                            Download
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleSdsDelete(sds.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* SDS Upload Dialog */}
            <Dialog open={showSdsUploadDialog} onOpenChange={setShowSdsUploadDialog}>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>{t('adminUI.UploadSafetyDataSheets')}</DialogTitle>
                  <DialogDescription>
                    Upload one or multiple SDS PDF files. For bulk upload, chemical names will be extracted from filenames.
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-4">
                  {/* File Selection Buttons */}
                  <div className="flex gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      className="flex-1"
                      onClick={() => document.getElementById('sds-file-input').click()}
                    >
                      <FileText className="mr-2 h-4 w-4" />
                      Select Files
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      className="flex-1 bg-green-50 hover:bg-green-100 border-green-300"
                      onClick={() => document.getElementById('sds-folder-input').click()}
                    >
                      📁 Bulk Upload (Folder)
                    </Button>
                  </div>

                  <input
                    id="sds-file-input"
                    type="file"
                    accept=".pdf"
                    multiple
                    onChange={handleSdsFileSelect}
                    className="hidden"
                  />
                  <input
                    id="sds-folder-input"
                    type="file"
                    webkitdirectory=""
                    directory=""
                    multiple
                    onChange={handleFolderSelect}
                    className="hidden"
                  />

                  {/* Show selected files */}
                  {(sdsUploadForm.file || sdsUploadForm.files.length > 0) && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                      <p className="text-sm font-medium text-green-800">
                        {sdsUploadForm.files.length > 0 
                          ? `${sdsUploadForm.files.length} PDF files selected`
                          : `Selected: ${sdsUploadForm.file?.name}`}
                      </p>
                      {sdsUploadForm.files.length > 1 && (
                        <p className="text-xs text-green-600 mt-1">{t('adminUI.ChemicalNamesWillBeAutoExtractedFromFilenames')}</p>
                      )}
                    </div>
                  )}

                  {/* Chemical name - only required for single file */}
                  <div>
                    <label className="text-sm font-medium">
                      Chemical/Product Name {sdsUploadForm.files.length <= 1 && '*'}
                    </label>
                    <input
                      type="text"
                      value={sdsUploadForm.chemicalName}
                      onChange={(e) => setSdsUploadForm({...sdsUploadForm, chemicalName: e.target.value})}
                      placeholder={sdsUploadForm.files.length > 1 ? "Optional for bulk upload" : "e.g., Acetone, Bleach, etc."}
                      className="w-full border rounded px-3 py-2 mt-1"
                    />
                    {sdsUploadForm.files.length > 1 && (
                      <p className="text-xs text-gray-500 mt-1">{t('adminUI.LeaveBlankToAutoExtractFromFilenames')}</p>
                    )}
                  </div>

                  <div>
                    <label className="text-sm font-medium">Manufacturer (Optional)</label>
                    <input
                      type="text"
                      value={sdsUploadForm.manufacturer}
                      onChange={(e) => setSdsUploadForm({...sdsUploadForm, manufacturer: e.target.value})}
                      placeholder="e.g., 3M, DuPont, etc."
                      className="w-full border rounded px-3 py-2 mt-1"
                    />
                  </div>
                </div>

                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowSdsUploadDialog(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleSdsUpload} disabled={uploadingSds}>
                    {uploadingSds ? 'Uploading...' : `Upload ${sdsUploadForm.files.length > 1 ? sdsUploadForm.files.length + ' Files' : 'SDS'}`}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default AdminPolicies;
